<script setup lang="ts">
import type { PropertyTypeHighlightClickPayload } from '~/components/Filters/PropertyTypeSelect';
import type { ShortsFilters } from '~/core/entities/property/filters';

import { useTrackSearchPropertyEvents } from '~/composables/events/useTrackSearchPropertyEvents';
import { useSearchSuggestGeoOnlyConfig } from '~/composables/searchSuggest/useSearchSuggestGeoOnlyConfig';
import { useShortsFilterChangeTracking } from '~/composables/useShortsFilterChangeTracking';
import { navigateToShortsLocationPath } from '~/composables/useShortsFilters/buildShortsLocationPath';
import { stripShortsLocationPathFromRoute } from '~/composables/useShortsFilters/stripShortsLocationQuery';
import { useHighlightedPropertyTypes } from '~/composables/useShortsFilters/useHighlightedPropertyTypes';
import { useShortsFilters } from '~/composables/useShortsFilters/useShortsFilters';
import { MixpanelProps } from '~/core/service/mixpanel';
import { cn } from '~/utils';
import { getAreaRangeLabel, getPriceRangeLabel } from '~~/utils';
import { cva } from 'class-variance-authority';

import FilterButton from '~/components/Filters/FilterButton/Button.vue';
import FilterDropdown from '~/components/Filters/FilterDropdown/Dropdown.vue';
import { FiltersSidebar } from '~/components/Filters/FiltersSidebar';
import PropertyArea from '~/components/Filters/PropertyArea/Area.vue';
import PropertyPrice from '~/components/Filters/PropertyPrice/Price.vue';
import PropertyTypeSelect from '~/components/Filters/PropertyTypeSelect/TypeSelect.vue';

import FilterIcon from '~icons/material-symbols-light/filter-list';

import { useShortsFiltersSearch } from './composables/useShortsFiltersSearch';
import { useShortsFiltersTracking } from './composables/useShortsFiltersTracking';
import { useShortsSidebarFilterGroups } from './composables/useShortsSidebarFilterGroups';
import { shortsBarContentContainerX } from './shortsHorizontalPadding';
import ShortsSearchSuggestDialog from './ShortsSearchSuggestDialog.vue';
import ShortsSearchSuggestInline from './ShortsSearchSuggestInline.vue';

const isShortsScrollCompact = useState('shorts-scroll-compact', () => false);
const scrollVariant = computed(() =>
	isShortsScrollCompact.value ? 'compact' : 'expanded'
);

/** z-40: abaixo do PortalHeader (z-100) para menus no desktop; sidebar continua z-[101]/102. */
/** when compact: before preenche a faixa acima da barra (header escondido → evita “buraco”). when expanded: sem before para o header (z-35) aparecer no mobile. */
const barScrollEase =
	'duration-200 ease-out lg:duration-300 lg:ease-[cubic-bezier(0.25,0.1,0.25,1)]';

const barWrapperVariants = cva(
	cn(
		'sticky z-40 flex shrink-0 flex-col bg-black transition-[top]',
		barScrollEase
	),
	{
		variants: {
			scroll: {
				compact:
					'top-0 before:absolute before:left-0 before:right-0 before:bottom-full before:h-[60px] before:bg-black before:content-[""] lg:before:h-20',
				expanded: 'top-[60px] lg:top-20'
			}
		}
	}
);

const barTitleVariants = cva(
	cn(
		'overflow-hidden bg-black transition-[max-height,padding-top,padding-bottom]',
		barScrollEase
	),
	{
		variants: {
			scroll: {
				compact: 'max-h-0 py-0',
				expanded: 'min-h-0 max-h-[4.5rem] lg:max-h-[5.25rem] pt-4 pb-0'
			}
		}
	}
);

const barSectionVariants = cva(
	cn(
		'relative flex w-full justify-center bg-black pb-0 md:pt-4 min-[960px]:pt-4 px-0 transition-[padding-top]',
		barScrollEase
	),
	{
		variants: {
			scroll: {
				compact: 'pt-2 md:pt-2 min-[960px]:pt-2',
				expanded: 'pt-4'
			}
		}
	}
);

interface ShortsFiltersBarProps {
	countProperties: number;
	isFetchingProperties?: boolean;
}

const props = withDefaults(defineProps<ShortsFiltersBarProps>(), {
	isFetchingProperties: false
});

const emit = defineEmits<{
	'preview-filters': [filters: ShortsFilters];
}>();

const {
	cities,
	regions,
	macroregions,
	minAskingPrice,
	maxAskingPrice,
	minArea,
	maxArea,
	propertyTypes,
	hasActiveFilters,
	clearFilters,
	clearLocation,
	local,
	syncLocalFromQuery,
	applySidebarFilters,
	toPreviewFilters,
	toShortsFiltersForApi,
	states,
	currentFiltersObject,
	locationLabel,
	selectionText
} = useShortsFilters();

// Track filter changes for event dispatch (watchers are setup inside the composable)
useShortsFilterChangeTracking({
	isFetchingProperties: () => props.isFetchingProperties,
	countProperties: () => props.countProperties,
	minPrice: minAskingPrice,
	maxPrice: maxAskingPrice,
	minArea,
	maxArea,
	toShortsFilters: () => toShortsFiltersForApi.value
});

function handleClearLocation() {
	clearLocation();
	navigateToCurrentFiltersPath();
}

function navigateToCurrentFiltersPath() {
	void navigateToShortsLocationPath({
		macroregions: macroregions.value,
		cities: cities.value,
		regions: regions.value,
		states: states.value,
		propertyTypes: propertyTypes.value,
		minAskingPrice: minAskingPrice.value,
		maxAskingPrice: maxAskingPrice.value,
		minArea: minArea.value,
		maxArea: maxArea.value
	});
}

function handlePropertyTypeUpdate(val: typeof propertyTypes.value) {
	propertyTypes.value = val;
	navigateToCurrentFiltersPath();
}

const { highlightedPropertyTypesFromShorts } = useHighlightedPropertyTypes(
	computed(() => local.propertyTypes)
);

const shortsSearchGeoOnly = useSearchSuggestGeoOnlyConfig();

const { shortsSearchSuggestBus, handleSearchTermUpdate } =
	useShortsFiltersSearch(cities, regions, macroregions, states, {
		countProperties: () => props.countProperties,
		isFetchingProperties: () => props.isFetchingProperties,
		onLocationSelected: navigateToCurrentFiltersPath
	});

const {
	takeSnapshot,
	handleTrackOpenFilterApplied,
	trackApplySidebarFilters,
	trackClearSidebarFilters
} = useShortsFiltersTracking(
	() => toShortsFiltersForApi.value,
	() => toPreviewFilters.value,
	() => props.countProperties,
	() => props.isFetchingProperties
);

const { trackSuggestionFilter, trackEmptyStateFilter } =
	useTrackSearchPropertyEvents();

function handlePropertyTypeHighlightClick(
	payload: PropertyTypeHighlightClickPayload
) {
	const otherSuggestions = highlightedPropertyTypesFromShorts.value
		.filter(h => h.value !== payload.item.value)
		.map(h => h.label)
		.join(', ');

	trackSuggestionFilter({
		[MixpanelProps.REGION_NAME]: '',
		[MixpanelProps.CITY_NAME]: '',
		[MixpanelProps.SUGGESTION_CLICKED]: payload.item.label,
		[MixpanelProps.SUGGESTION_POSITION]: payload.index + 1,
		[MixpanelProps.OTHER_SUGGESTIONS]: otherSuggestions,
		[MixpanelProps.TOTAL_RESULTS]: props.countProperties
	});
}

function handleEmptyResultOnFilter(searchTerm: string) {
	trackEmptyStateFilter({
		[MixpanelProps.TEXT_SEARCH]: searchTerm
	});
}

const sidebarFilters = useShortsSidebarFilterGroups(
	local,
	highlightedPropertyTypesFromShorts,
	handlePropertyTypeHighlightClick
);

const isSidebarActive = ref(false);
const isSearchDialogOpen = ref(false);

const bodyElement = ref<HTMLElement | null>(null);
const isBodyScrollLocked = useScrollLock(bodyElement);

let hasOpenedSidebar = false;

watch(isSidebarActive, isActive => {
	isBodyScrollLocked.value = isActive;
	if (isActive) {
		hasOpenedSidebar = false;
	}
});

watch(
	() => [
		local.minAskingPrice,
		local.maxAskingPrice,
		local.minArea,
		local.maxArea,
		local.propertyTypes
	],
	() => {
		if (!isSidebarActive.value) return;
		if (!hasOpenedSidebar) {
			hasOpenedSidebar = true;
			return;
		}
		emit('preview-filters', toPreviewFilters.value);
	},
	{ deep: true }
);

onMounted(() => {
	bodyElement.value = document.body;
});

const getPriceLabel = () =>
	getPriceRangeLabel(
		minAskingPrice.value ?? undefined,
		maxAskingPrice.value ?? undefined
	);

const numberOfPriceFilters = computed(() => {
	let count = 0;
	if (minAskingPrice.value && !isNaN(minAskingPrice.value)) count++;
	if (maxAskingPrice.value && !isNaN(maxAskingPrice.value)) count++;
	return count;
});

const getAreaLabel = () =>
	getAreaRangeLabel(minArea.value ?? undefined, maxArea.value ?? undefined);

const numberOfAreaFilters = computed(() => {
	let count = 0;
	if (minArea.value && !isNaN(minArea.value)) count++;
	if (maxArea.value && !isNaN(maxArea.value)) count++;
	return count;
});

const sidebarApplyButtonText = computed(() => {
	if (props.countProperties === 0) return 'Nenhum short encontrado';
	const label = props.countProperties === 1 ? 'short' : 'shorts';
	return `Mostrar ${props.countProperties} ${label}`;
});

function handleOpenSidebar() {
	syncLocalFromQuery();
	isSidebarActive.value = true;
	nextTick(() => {
		hasOpenedSidebar = true;
	});
}

function handleApplySidebarFilters() {
	trackApplySidebarFilters();
	applySidebarFilters();
	navigateToCurrentFiltersPath();
	isSidebarActive.value = false;
}

function handleClearSidebarFilters() {
	trackClearSidebarFilters();
	clearFilters();
	stripShortsLocationPathFromRoute();
	isSidebarActive.value = false;
}
</script>

<template>
	<div :class="barWrapperVariants({ scroll: scrollVariant })">
		<div :class="cn('w-full bg-black', shortsBarContentContainerX)">
			<div :class="barTitleVariants({ scroll: scrollVariant })">
				<h1
					class="font-matter-sq text-[24px] font-normal leading-[150%] tracking-[-0.02em] text-white"
				>
					Shorts
				</h1>
			</div>

			<section :class="barSectionVariants({ scroll: scrollVariant })">
				<div
					class="flex w-full flex-nowrap items-center justify-start gap-x-2 md:gap-x-3"
				>
					<div class="flex min-w-0 flex-1 items-center gap-x-3">
						<div
							class="relative flex min-w-0 flex-1 max-[960px]:w-full sm:w-full min-[960px]:w-[400px] min-[960px]:flex-none h-11 md:h-[50px]"
						>
							<!-- Mobile: SearchSuggestDialog -->
							<div class="size-full lg:hidden">
								<ShortsSearchSuggestDialog
									v-model:is-open="isSearchDialogOpen"
									v-bind="shortsSearchGeoOnly"
									variant="dark"
									:search-term="selectionText"
									:current-filters="currentFiltersObject"
									:cities="cities"
									:regions="regions"
									:macroregions="macroregions"
									:states="states"
									class="w-full border-none shadow-[0_1px_3px_1px_#00000026]"
									input-wrapper-classes="bg-primary-light-4 border border-primary-light-3 hover:border-white-dark-4 focus-within:border-white-dark-4 pr-3"
									button-input-classes="pr-3 pl-2 h-full hover:bg-primary-light-4 text-sm"
									input-classes="min-h-16 text-white text-base"
									input-search-icon-wrapper-classes="hidden"
									list-wrapper-classes="mt-[11px] md:mt-3 lg:mt-4 z-50"
									:placeholders="locationLabel || 'Busque lugares'"
									focused-placeholder="Busque lugares"
									:bus="shortsSearchSuggestBus"
									@update:search-term="handleSearchTermUpdate"
									@empty-result-on-filter="handleEmptyResultOnFilter"
									@clear="handleClearLocation"
								>
									<template #input-prefix>
										<button
											type="button"
											class="border-none bg-transparent pl-3"
											@click="isSearchDialogOpen = true"
										>
											<IconMaterialSymbolsLightSearch
												class="size-5 text-beige"
											/>
										</button>
									</template>
									<template #trigger="{ open }">
										<div
											class="flex min-w-0 flex-1 items-center gap-x-0"
											data-testid="shorts-search-trigger-row"
										>
											<button
												type="button"
												class="z-10 flex h-11 min-w-0 flex-1 items-center rounded-md bg-primary-light-4 pr-3 pl-2 text-white hover:bg-primary-light-4/90 max-md:border-y max-md:border-primary-light-3 max-md:hover:border-white-dark-4"
												@click="open"
											>
												<span
													:class="
														cn(
															'truncate text-left text-sm',
															selectionText ? 'text-white' : 'text-white-dark-4'
														)
													"
												>
													{{ selectionText || 'Busque lugares' }}
												</span>
											</button>
											<div class="ml-auto flex shrink-0 items-center">
												<div
													v-show="!!selectionText"
													class="flex h-11 w-7 items-center justify-end"
												>
													<IconMaterialSymbolsLightCancel
														class="size-5 min-w-5 cursor-pointer text-primary-light-3"
														@click.stop="
															handleClearLocation();
															isSearchDialogOpen = false;
														"
													/>
												</div>
											</div>
										</div>
									</template>
								</ShortsSearchSuggestDialog>
							</div>

							<!-- Desktop: busca local Shorts -->
							<div class="hidden size-full lg:block">
								<ShortsSearchSuggestInline
									:search-term="selectionText"
									:location-label="locationLabel"
									:cities="cities"
									:regions="regions"
									:macroregions="macroregions"
									:states="states"
									:current-filters="currentFiltersObject"
									:bus="shortsSearchSuggestBus"
									@update:search-term="handleSearchTermUpdate"
									@empty-result-on-filter="handleEmptyResultOnFilter"
									@clear="handleClearLocation"
								>
									<template #input-prefix>
										<div class="flex h-full items-center pl-3">
											<IconMaterialSymbolsLightSearch
												class="size-5 text-beige"
											/>
										</div>
									</template>
								</ShortsSearchSuggestInline>
							</div>
						</div>

						<!-- Desktop: PropertyPrice dropdown -->
						<div class="hidden min-[960px]:flex">
							<FilterDropdown
								data-testid="shorts-price-filter-dropdown"
								:label="getPriceLabel()"
								:number-of-applied-filters="numberOfPriceFilters"
								:show-number-of-applied-filters="false"
								filter-button-variant="dark"
								content-variant="black"
								class="hidden min-[960px]:flex"
								@open="takeSnapshot('pricing')"
								@close="handleTrackOpenFilterApplied('pricing')"
							>
								<template #content>
									<PropertyPrice
										v-model:min="minAskingPrice"
										v-model:max="maxAskingPrice"
										variant="dark"
									/>
								</template>
							</FilterDropdown>
						</div>

						<!-- Desktop: PropertyArea dropdown -->
						<div class="hidden min-[960px]:flex">
							<FilterDropdown
								data-testid="shorts-area-filter-dropdown"
								:label="getAreaLabel()"
								:number-of-applied-filters="numberOfAreaFilters"
								:show-number-of-applied-filters="false"
								filter-button-variant="dark"
								content-variant="black"
								class="hidden min-[960px]:flex"
								@open="takeSnapshot('area')"
								@close="handleTrackOpenFilterApplied('area')"
							>
								<template #content>
									<PropertyArea
										v-model:min="minArea"
										v-model:max="maxArea"
										variant="dark"
									/>
								</template>
							</FilterDropdown>
						</div>

						<!-- Desktop: PropertyTypeSelect dropdown -->
						<div class="hidden min-[960px]:flex xl:flex">
							<FilterDropdown
								data-testid="shorts-type-filter-dropdown"
								label="Tipo do imóvel"
								:number-of-applied-filters="propertyTypes.length"
								:show-number-of-applied-filters="false"
								filter-button-variant="dark"
								content-variant="black"
								class="hidden min-[960px]:flex xl:flex"
								@open="takeSnapshot('propertyTypes')"
								@close="handleTrackOpenFilterApplied('propertyTypes')"
							>
								<template #label>
									<div class="flex items-center gap-1">
										<p>Tipo do imóvel</p>
										<div
											v-show="propertyTypes.length"
											class="size-5 min-w-5 rounded-full bg-primary-light-1 text-center text-xs leading-5 text-white"
										>
											{{ propertyTypes.length }}
										</div>
									</div>
								</template>
								<template #content>
									<PropertyTypeSelect
										:model-value="propertyTypes"
										variant="dark"
										@update:model-value="handlePropertyTypeUpdate"
										@click-highlight-item="handlePropertyTypeHighlightClick"
									/>
								</template>
							</FilterDropdown>
						</div>

						<!-- Mobile + fallback: Sidebar filter button -->
						<div
							class="ml-auto transition-all duration-300 sm:max-w-none md:ml-[unset] min-[960px]:hidden"
						>
							<FilterButton
								v-model:is-active="isSidebarActive"
								data-testid="shorts-sidebar-filter-button"
								variant="dark-reverse"
								:number-of-applied-filters="hasActiveFilters ? 1 : 0"
								:show-number-of-applied-filters="false"
								@click="handleOpenSidebar"
							>
								<template #icon>
									<FilterIcon class="order-last size-6 text-beige" />
								</template>
								<template #label>
									<div class="order-1 hidden sm:flex">
										<p>Filtros</p>
									</div>
								</template>
							</FilterButton>
						</div>
					</div>
				</div>
			</section>
		</div>
		<div class="h-4 w-full shrink-0 bg-black min-[960px]:bg-primary" />

		<!--
			ClientOnly: Teleport + Transition com v-if gera mismatch de hidratação (SSR vs cliente).
		-->
		<ClientOnly>
			<Teleport to="body">
				<Transition
					enter-active-class="transition-opacity duration-300 ease-out"
					enter-from-class="opacity-0"
					enter-to-class="opacity-100"
					leave-active-class="transition-opacity duration-300 ease-in"
					leave-from-class="opacity-100"
					leave-to-class="opacity-0"
				>
					<div
						v-if="isSidebarActive"
						class="fixed inset-0 z-[35] hidden h-screen w-screen bg-black/40 md:flex"
						@click="isSidebarActive = false"
					/>
				</Transition>
			</Teleport>
		</ClientOnly>

		<ClientOnly>
			<FiltersSidebar
				v-model:is-active="isSidebarActive"
				variant="dark"
				:track-events="false"
				page-name="ShortsHomeFeed"
				:filters="{}"
				:filter-components="sidebarFilters"
				:is-fetching-properties="isFetchingProperties"
				:count-properties="countProperties"
				:apply-button-text="sidebarApplyButtonText"
				use-external-count
				class="z-[102]"
				@apply-filters="handleApplySidebarFilters"
				@clear-filters="handleClearSidebarFilters"
			/>
		</ClientOnly>
	</div>
</template>
